<template>
  <v-data-table-server
    v-if="hasServerSidedPagination"
    :id="tableId"
    v-model:items-per-page="currentItemsPerPage"
    :headers="headers"
    :fixed-header="fixedHeader"
    :fixed-footer="fixedFooter"
    :max-height="maxHeight"
    :items="tblItems"
    :items-length="totalItems"
    :loading="loading"
    :hover="true"
    :multi-sort="hasMultisort"
    :sort-by="sortBy"
    :search="search"
    :items-per-page-options="itemsPerPageOptions"
    density="comfortable"
    :style="maxHeightValue"
    @click:row="selectItem"
    @update:options="loadItems"
  >
    <template #item.name="{ item }">
      <span class="ml-0 pl-0" data-testid="table-name-column" style="font-weight: bold">
        {{ item.name }}
      </span>
    </template>

    <template #item.iconprovider="{ item }">
      <ProviderIcon
        v-if="item.iconprovider"
        :provider="item.iconprovider.provider"
        class="mr-2"
        :artifact-type-color="item.iconprovider.artifactTypeColor"
      />
    </template>
    <template #item.selected="{ item }">
      <v-checkbox-btn
        v-model="item.selected"
        :style="item.selected ? '' : 'visibility: hidden;'"
        :class="item.selected ? 'selected-table-item' : ''"
        readonly
      />
    </template>
    <template #item.actions="{ item }">
      <v-btn
        v-if="hasApplyAction"
        class="table-apply-action pt-0 mt-0"
        density="compact"
        variant="plain"
        icon
        @click.stop="applyItem(item)"
      >
        <v-icon color="black" style="position: absolute">
          {{ CONSTANTS.ICONS_MDI.ICON_CHECK }}
        </v-icon>
        <v-tooltip activator="parent" location="bottom">
          {{ tooltipText(labels.ANALYZE_DATA_TABLE.APPLY) }}
        </v-tooltip>
      </v-btn>

      <v-btn
        v-if="hasEditAction"
        class="table-edit-action pt-0 mt-0"
        density="compact"
        variant="plain"
        icon
        @click.stop="editItem(item)"
      >
        <v-icon size="small" color="black" style="position: absolute">
          {{ CONSTANTS.ICONS_MDI.ICON_EDIT }}
        </v-icon>
        <v-tooltip activator="parent" location="bottom">
          {{ tooltipText(labels.ANALYZE_DATA_TABLE.EDIT) }}
        </v-tooltip>
      </v-btn>

      <v-btn
        v-if="hasDeleteAction"
        class="table-delete-action pt-0 mt-0 mr-5"
        density="compact"
        variant="plain"
        icon
        @click.stop="deleteItem(item)"
      >
        <v-icon size="small" color="black" style="position: absolute">
          {{ CONSTANTS.ICONS_MDI.ICON_DELETE_OUTLINE }}
        </v-icon>
        <v-tooltip activator="parent" location="bottom">
          {{ tooltipText(labels.ANALYZE_DATA_TABLE.DELETE) }}
        </v-tooltip>
      </v-btn>
    </template>
  </v-data-table-server>
  <v-data-table
    v-else
    :id="tableId"
    v-model:items-per-page="currentItemsPerPage"
    :class="shouldHideFooter ? 'hidden-footer' : ''"
    :headers="headers"
    :fixed-header="fixedHeader"
    :fixed-footer="fixedFooter"
    :max-height="maxHeight"
    :items="tblItems"
    :items-length="totalItems"
    :loading="loading"
    :hover="true"
    :multi-sort="hasMultisort"
    :sort-by="sortBy"
    :search="search"
    :items-per-page-options="itemsPerPageOptions"
    :custom-filter="(_: any, query: any, item: any) => filterNames(_, query, item)"
    density="comfortable"
    :style="maxHeightValue"
    @click:row="selectItem"
    @update:options="loadItems"
  >
    <template v-if="hasFavorites" #item.favorite="{ item }">
      <v-checkbox-btn
        v-model="item.favorite"
        data-testid="table-favorite-item"
        :class="item.favorite ? 'favorites-icon-color' : ''"
        :true-icon="CONSTANTS.ICONS_MDI.ICON_STAR"
        :false-icon="CONSTANTS.ICONS_MDI.ICON_STAR_OUTLINE"
        @click.stop="toggleFavorite(item)"
      />
    </template>

    <template #item.iconprovider="{ item }">
      <ProviderIcon
        v-if="item.iconprovider"
        :provider="item.iconprovider.provider"
        class="mr-2"
        :artifact-type-color="(item.iconprovider as ArtifactItem).artifactTypeColor"
      />
    </template>

    <template #item.artifactitem="{ item }">
      <div class="artifact-text">
        <div>{{ (item.artifactitem as ArtifactItem).name }}</div>
        <div class="artifact-text">
          {{ (item.artifactitem as ArtifactItem).artifactType }}
        </div>
      </div>
    </template>

    <template #item.selected="{ item }">
      <v-checkbox-btn
        v-model="item.selected"
        :style="item.selected ? '' : 'visibility: hidden;'"
        :class="item.selected ? 'selected-table-item' : ''"
        readonly
      />
    </template>

    <template #item.name="{ item }">
      <span
        class="ml-0 pl-0"
        data-testid="table-name-column"
        style="font-weight: bold"
        @click.right.prevent="openActionItemsMenu(item, $event)"
      >
        {{ item.name }}
      </span>
      <v-menu v-model="actionItemsMenu" :style="menuPosition" location="start">
        <v-card class="mx-auto slight-shadow" max-width="350" variant="elevated">
          <v-card-item class="pa-1">
            <v-list density="compact">
              <v-list-item
                v-if="hasApplyAction"
                id="analyze-data-table-menu-item-apply"
                class="context-menu-item"
                :prepend-icon="CONSTANTS.ICONS_MDI.ICON_CHECK"
                @click="applyItem(itemFromContextmenu)"
              >
                <v-list-item-title class="pl-3">
                  {{ labels.ANALYZE_DATA_TABLE.APPLY }}
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                v-if="hasEditAction"
                id="analyze-data-table-menu-item-edit"
                class="context-menu-item"
                :prepend-icon="CONSTANTS.ICONS_MDI.ICON_EDIT"
                @click="editItem(itemFromContextmenu)"
              >
                <v-list-item-title class="pl-3">
                  {{ labels.ANALYZE_DATA_TABLE.EDIT }}
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                v-if="hasDeleteAction"
                id="analyze-data-table-menu-item-delete"
                class="context-menu-item"
                :prepend-icon="CONSTANTS.ICONS_MDI.ICON_DELETE_OUTLINE"
                @click="deleteItem(itemFromContextmenu)"
              >
                <v-list-item-title class="pl-3">
                  {{ labels.ANALYZE_DATA_TABLE.DELETE }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card-item>
        </v-card>
      </v-menu>
    </template>
    <template #item.updated="{ item }">
      <span>{{ formatLocalDateTime(item.updated as Date) }}</span>
    </template>

    <template #item.actions="{ item }">
      <v-btn
        v-if="hasApplyAction"
        class="table-apply-action pt-0 mt-0"
        density="compact"
        variant="plain"
        icon
        @click.stop="applyItem(item)"
      >
        <v-icon color="black" style="position: absolute">
          {{ CONSTANTS.ICONS_MDI.ICON_CHECK }}
        </v-icon>
        <v-tooltip activator="parent" location="bottom">
          {{ tooltipText(labels.ANALYZE_DATA_TABLE.APPLY) }}
        </v-tooltip>
      </v-btn>

      <v-btn
        v-if="hasEditAction"
        class="table-edit-action pt-0 mt-0"
        density="compact"
        variant="plain"
        icon
        @click.stop="editItem(item)"
      >
        <v-icon size="small" color="black" style="position: absolute">
          {{ CONSTANTS.ICONS_MDI.ICON_EDIT }}
        </v-icon>
        <v-tooltip activator="parent" location="bottom">
          {{ tooltipText(labels.ANALYZE_DATA_TABLE.EDIT) }}
        </v-tooltip>
      </v-btn>

      <v-btn
        v-if="hasDeleteAction"
        class="table-delete-action pt-0 mt-0 mr-5"
        density="compact"
        variant="plain"
        icon
        @click.stop="deleteItem(item)"
      >
        <v-icon size="small" color="black" style="position: absolute">
          {{ CONSTANTS.ICONS_MDI.ICON_DELETE_OUTLINE }}
        </v-icon>
        <v-tooltip activator="parent" location="bottom">
          {{ tooltipText(labels.ANALYZE_DATA_TABLE.DELETE) }}
        </v-tooltip>
      </v-btn>
    </template>
    <template #header.deleteAction="{}">
      <v-row>
        <v-spacer></v-spacer>
        <v-btn
          class="delete-attribute-table-action"
          variant="text"
          density="compact"
          icon
          size="small"
          @click="deleteTable()"
        >
          <v-icon>{{ CONSTANTS.ICONS_MDI.ICON_DELETE }}</v-icon>
        </v-btn>
      </v-row>
    </template>
  </v-data-table>
</template>

<script lang="ts" setup generic="T">
  import {
    AnalyzeTableHeader,
    AnalyzeTableItem,
    TableDataType,
    SortItem,
    SelectedTableItem,
    InternalItem,
    PaginationOptions,
  } from '@/models/table.model'
  import { formatLocalDateTime } from '@/utils/helpers'
  import CONSTANTS from '@/constants/Config.constants'
  import labels from '@/translations/labels.json'
  import ProviderIcon from '@/components/ProviderIcon/ProviderIcon.vue'
  import { ArtifactItem } from '@/services/ISearchService'
  import { PropType, onMounted, ref, watch, nextTick, computed } from 'vue'
  import { Ref } from 'vue'

  const actionItemsMenu = ref(false)
  const mouseCoordX = ref(0)
  const mouseCoordY = ref(0)
  const menuPosition = ref('')
  const itemFromContextmenu = ref<AnalyzeTableItem<T>>()

  const props = defineProps({
    headers: { type: Object as () => AnalyzeTableHeader[], required: true },
    items: { type: Object as () => AnalyzeTableItem<T>[], required: true },
    tableId: { type: String, required: true },
    dataType: { type: String as () => TableDataType, default: 'default' },
    hasFavorites: { type: Boolean, default: false },
    hasDeleteAction: { type: Boolean, default: false },
    hasEditAction: { type: Boolean, default: false },
    hasApplyAction: { type: Boolean, default: false },
    hasMultisort: { type: Boolean, default: false },
    maxHeight: { type: Number, default: 0 },
    fixedHeader: { type: Boolean, default: false },
    fixedFooter: { type: Boolean, default: false },
    itemsPerPage: { type: Number, default: 10 },
    sortBy: { type: Object as PropType<SortItem[]>, default: undefined },
    totalItems: { type: Number, default: 1 },
    loading: { type: Boolean, default: false },
    loadItems: {
      type: Function as PropType<(options: any) => any>,
      default: () => () => {
        // Implementation or noop
      },
    },
    search: { type: String, default: '' },
    hasServerSidedPagination: { type: Boolean, default: false },
  })

  const emit = defineEmits<{
    apply: [value: T]
    select: [value: T]
    edit: [value: T]
    delete: [value: T]
    favorite: [value: T]
    deleteTable: []
  }>()

  onMounted(() => {
    document.querySelectorAll('.v-table tbody tr td').forEach((elem: Element) => {
      elem.addEventListener('contextmenu', (e: Event) => e.preventDefault())
    })
  })

  watch(
    () => props.items,
    () => {
      document.querySelectorAll('.v-table tbody tr td').forEach((elem: Element) => {
        elem.addEventListener('contextmenu', (e: Event) => e.preventDefault())
      })
    },
    { immediate: true },
  )

  const tblItems = ref<AnalyzeTableItem<T>[]>([]) as Ref<AnalyzeTableItem<T>[]>
  const currentItemsPerPage = ref<number>()

  let itemsPerPageOptions: PaginationOptions[]

  if (!props.hasServerSidedPagination) {
    itemsPerPageOptions = <PaginationOptions[]>[
      { value: 5, title: '5' },
      { value: 10, title: '10' },
      { value: 25, title: '25' },
      { value: 50, title: '50' },
      { value: -1, title: '$vuetify.dataFooter.itemsPerPageAll' },
    ]
  } else {
    itemsPerPageOptions = <PaginationOptions[]>[
      { value: 5, title: '5' },
      { value: 10, title: '10' },
      { value: 25, title: '25' },
      { value: 50, title: '50' },
    ]
  }
  const maxHeightValue = computed((): string => {
    if (props.maxHeight !== 0) {
      return 'max-Height: ' + props.maxHeight + 'vh'
    } else {
      return 'height: 100%'
    }
  })

  watch(
    () => props.items,
    () => {
      tblItems.value = props.items
    },
    { immediate: true },
  )

  watch(
    () => props.itemsPerPage,
    () => {
      currentItemsPerPage.value = props.itemsPerPage
    },
    { immediate: true },
  )

  const shouldHideFooter = computed((): boolean => {
    return props.totalItems <= itemsPerPageOptions[0].value
  })

  function tooltipText(action: string): string {
    switch (props.dataType) {
      case 'projects':
        return `${action} ${labels.ANALYZE_DATA_TABLE.PROJECT}`
      case 'snapshots':
        return `${action} ${labels.ANALYZE_DATA_TABLE.SNAPSHOT}`
      case 'filters':
        return `${action} ${labels.ANALYZE_DATA_TABLE.FILTER}`
      default:
        return action
    }
  }

  function applyItem(tableItemToApply?: AnalyzeTableItem<T>): void {
    if (tableItemToApply && tableItemToApply.value) {
      emit('apply', tableItemToApply.value)
    }
  }

  function editItem(tableItem?: AnalyzeTableItem<T>): void {
    if (tableItem && tableItem.value) {
      emit('edit', tableItem.value)
    }
  }

  function deleteItem(tableItem?: AnalyzeTableItem<T>): void {
    if (tableItem && tableItem.value) {
      emit('delete', tableItem.value)
    }
  }

  function deleteTable(): void {
    emit('deleteTable')
  }

  function selectItem(_: Event, tableItem: SelectedTableItem<T>): void {
    switch (props.dataType) {
      case 'filters':
        // selecting a filter leads to the edit mode
        if (tableItem.item.value) emit('edit', tableItem.item.value)
        break
      default:
        if (tableItem.item.value) {
          emit('select', tableItem.item.value)
          break
        }
        if (tableItem.item.value) emit('select', tableItem.item.value)
        break
    }
  }

  function toggleFavorite(item: AnalyzeTableItem<T>): void {
    if (item.value) emit('favorite', item.value)
  }

  function filterNames(
    _: string,
    query: string,
    item: InternalItem<AnalyzeTableItem<T>> | undefined,
  ): boolean {
    if (item && item.raw && typeof item.raw.name === 'string') {
      return item.raw.name.toLowerCase().includes(query.toLowerCase())
    }

    return false
  }

  function openActionItemsMenu(item: AnalyzeTableItem<T>, event: MouseEvent): void {
    event.preventDefault()

    const scrollX = window.scrollX
    const scrollY = window.scrollY

    mouseCoordX.value = event.clientX + scrollX
    mouseCoordY.value = event.clientY + scrollY

    menuPosition.value = `top: ${mouseCoordY.value}px; left: ${mouseCoordX.value}px;`
    itemFromContextmenu.value = item

    nextTick(() => {
      actionItemsMenu.value = true
    })
  }
</script>

<style scoped>
  .v-table::v-deep {
    tbody tr {
      &:hover {
        background-color: rgba(var(--v-theme-itemis-analyze-secondary), 0.5) !important;
      }
    }

    tbody tr:has(div.selected-table-item) {
      background-color: rgb(var(--v-theme-itemis-analyze-secondary)) !important;
    }

    tbody tr td:has(input) {
      padding-left: 0px !important;
      padding-right: 0px !important;
    }

    tbody tr td div:has(input) {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    tbody tr:not(:hover) button.table-delete-action,
    tr:not(:hover) button.table-edit-action,
    tr:not(:hover) button.table-apply-action {
      visibility: hidden;
    }

    tbody tr td button.table-delete-action {
      border-color: transparent;
      border-radius: 3px;
      border-style: solid;
      border-width: 15px;

      &:hover {
        border-color: white;
        border-radius: 3px;
        border-style: solid;
        border-width: 15px;

        i {
          color: red !important;
        }
      }
    }

    tbody tr td button.table-edit-action,
    button.table-apply-action {
      border-color: transparent;
      border-radius: 3px;
      border-style: solid;
      border-width: 15px;

      &:hover {
        border-color: white;
        border-radius: 3px;
        border-style: solid;
        border-width: 15px;
      }
    }

    .favorites-icon-color {
      color: rgb(var(--v-theme-itemis-analyze-primary));
    }

    .hidden-footer.v-table .v-data-table-footer {
      display: none;
    }

    .slight-shadow {
      box-shadow: 0 1px 1px rgba(0, 0, 0, 0.1) !important;
    }

    .artifact-text {
      color: gray;
    }

    .context-menu-item .v-list-item__spacer {
      display: none;
    }
  }
</style>
